
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Navbar } from '@/components/Navbar';
import Footer from '@/components/Footer';
import { ArrowLeft, Calendar, User, Tag } from 'lucide-react';
import { blogPosts } from '@/data/blogPosts';
import { Button } from '@/components/ui/button';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  
  useEffect(() => {
    // Find the current post based on slug
    const currentPost = blogPosts.find(post => post.slug === slug);
    setPost(currentPost);
    
    if (currentPost) {
      // Find related posts with the same category, excluding current post
      const related = blogPosts
        .filter(p => p.id !== currentPost.id && p.categories.some(cat => 
          currentPost.categories.includes(cat)))
        .slice(0, 3);
      setRelatedPosts(related);
    }
  }, [slug]);
  
  if (!post) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">Blog post not found</h2>
            <Link to="/blog">
              <Button>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Blog
              </Button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-grow">
        {/* Hero Section with Cover Image */}
        <div className="w-full h-[40vh] md:h-[50vh] relative overflow-hidden">
          <img 
            src={post.coverImage} 
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <div className="text-center text-white px-4 max-w-4xl">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 drop-shadow-md">
                {post.title}
              </h1>
            </div>
          </div>
        </div>
        
        {/* Post Content */}
        <div className="container px-4 md:px-6 py-12">
          <div className="max-w-3xl mx-auto">
            {/* Back to Blog link */}
            <Link to="/blog" className="inline-flex items-center text-primary hover:underline mb-6">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blog
            </Link>
            
            {/* Post meta data */}
            <div className="flex flex-wrap items-center text-sm text-muted-foreground mb-8 gap-4">
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-1" />
                <span>{post.date}</span>
              </div>
              <div className="flex items-center">
                <User className="h-4 w-4 mr-1" />
                <span>{post.author}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {post.categories.map(category => (
                  <span 
                    key={category} 
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary"
                  >
                    <Tag className="h-3 w-3 mr-1" />
                    {category}
                  </span>
                ))}
              </div>
            </div>
            
            {/* Post content */}
            <div className="prose prose-lg max-w-none">
              <div dangerouslySetInnerHTML={{ __html: post.content }} />
            </div>
            
            {/* Share buttons would go here */}
            <div className="border-t border-b border-border py-6 my-8">
              <div className="flex justify-between items-center">
                <span className="font-medium">Share this post:</span>
                <div className="flex space-x-3">
                  {/* Placeholder for social sharing buttons */}
                  <Button variant="outline" size="sm">Twitter</Button>
                  <Button variant="outline" size="sm">Facebook</Button>
                  <Button variant="outline" size="sm">LinkedIn</Button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <div className="max-w-5xl mx-auto mt-12">
              <h3 className="text-2xl font-bold mb-6">Related Articles</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedPosts.map(relatedPost => (
                  <Link 
                    key={relatedPost.id} 
                    to={`/blog/${relatedPost.slug}`}
                    className="group block overflow-hidden rounded-lg border border-gray-200 hover:shadow-md transition-all duration-200"
                  >
                    <div className="aspect-video overflow-hidden bg-gray-100">
                      <img 
                        src={relatedPost.coverImage} 
                        alt={relatedPost.title} 
                        className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4">
                      <div className="text-sm text-muted-foreground mb-2">{relatedPost.date}</div>
                      <h4 className="text-lg font-semibold mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                        {relatedPost.title}
                      </h4>
                      <div className="text-sm text-primary group-hover:underline">Read article</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default BlogPost;
